/**
 * 縦メニュー（パソコン ≥ 960px）・下部タブ（スマホ）・メニューのシート（スマホの全ページ一覧）。
 * 今いる画面は aria-current="page"（M-35）。権限の無い項目は出さない。
 */
import type { MouseEvent } from "react";
import { NAV, navItemVisible, type Route, type NavItem } from "../routes.js";
import { useI18n, type Tr } from "../useI18n.js";
import { can, type Me } from "../useMe.js";
import { Icon, Logo } from "../ui/Icon.js";
import { Sheet } from "../ui/Sheet.js";
import { useShell } from "../ui/shell.js";
import { useNotifications } from "../notifStore.js";
import { LangSwitch } from "../pages/Login.js";
import { initials } from "../ui/Person.js";

function visibleGroups(me: Me) {
  return NAV.map((g) => ({ ...g, items: g.items.filter((it) => navItemVisible(it, me)) })).filter((g) => g.items.length > 0);
}

function NavLink({ item, route, onNavigate, t, count }: {
  item: NavItem; route: Route; onNavigate: (e: MouseEvent, href: string) => void; t: Tr; count?: number;
}) {
  const current = item.routes.includes(route.name);
  return (
    <a className="nav-link" href={item.href} aria-current={current ? "page" : undefined}
       onClick={(e) => onNavigate(e, item.href)}>
      <Icon name={item.icon} />
      <span>{t(item.label)}</span>
      {count !== undefined && count > 0 && (
        <span className="nav-count badge tone-danger" aria-label={t("notif.unreadCount", { n: count })}>{count > 99 ? "99+" : count}</span>
      )}
    </a>
  );
}

function useNavigate() {
  const { go } = useShell();
  return (e: MouseEvent, href: string) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    go(href);
  };
}

export function Sidebar({ me, route, onLogout }: { me: Me; route: Route; onLogout: () => void }) {
  const { t, lang, setLang } = useI18n();
  const { unread } = useNotifications();
  const nav = useNavigate();
  const { openSearch } = useShell();
  const isMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  return (
    <div className="sidebar">
      <a className="brand" href="#/" onClick={(e) => nav(e, "#/")}><Logo /> {t("app.title")}</a>
      {/* 横断検索の入口（⌘K / Ctrl+K でも開く） */}
      <button type="button" className="nav-search" onClick={openSearch} aria-keyshortcuts="Meta+K Control+K">
        <Icon name="search" size={20} /><span>{t("search.open")}</span><kbd className="kbd" aria-hidden="true">{isMac ? "⌘K" : "Ctrl K"}</kbd>
      </button>
      <nav aria-label={t("nav.label")}>
        {visibleGroups(me).map((g, i) => (
          <div className="nav-group" key={g.label ?? i}>
            {g.label && <p className="nav-group-label" id={`navg-${i}`}>{t(g.label)}</p>}
            <ul className="nav-list" aria-labelledby={g.label ? `navg-${i}` : undefined}>
              {g.items.map((it) => (
                <li key={it.href}>
                  <NavLink item={it} route={route} onNavigate={nav} t={t} count={it.href === "#/notifications" ? unread : undefined} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="sidebar-foot">
        {/* M-35: 自分の設定への入口。名前 / 設定 */}
        <a className="nav-link" href="#/settings" aria-current={route.name === "settings" ? "page" : undefined} onClick={(e) => nav(e, "#/settings")}>
          <span className="avatar" aria-hidden="true">{initials(me.displayName)}</span>
          <span className="truncate">{me.displayName}<span className="muted"> / {t("nav.settings")}</span></span>
        </a>
        <div className="cluster">
          <LangSwitch lang={lang} setLang={setLang} />
          <button type="button" className="icon-btn" onClick={onLogout} aria-label={t("common.logout")} title={t("common.logout")}><Icon name="logout" /></button>
        </div>
      </div>
    </div>
  );
}

/** 下部タブ: ホーム / 通知（未読数）/ 報告（中央・目立たせる）/ 申請 / メニュー */
export function TabBar({ route, onMenu, menuOpen }: { route: Route; onMenu: () => void; menuOpen: boolean }) {
  const { t } = useI18n();
  const { unread } = useNotifications();
  const nav = useNavigate();
  const tab = (href: string, names: Route["name"][], icon: Parameters<typeof Icon>[0]["name"], label: string, extra = "", badge?: number) => (
    <a className={`tab${extra}`} href={href} aria-current={names.includes(route.name) ? "page" : undefined}
       onClick={(e) => nav(e, href)}>
      <span className="tab-icon"><Icon name={icon} /></span>
      <span>{label}</span>
      {badge !== undefined && badge > 0 && <span className="badge-dot" aria-label={t("notif.unreadCount", { n: badge })}>{badge > 99 ? "99+" : badge}</span>}
    </a>
  );
  return (
    <nav className="tabbar" aria-label={t("nav.tabs")}>
      {tab("#/", ["home"], "home", t("nav.home"))}
      {tab("#/notifications", ["notifications"], "bell", t("notif.title"), "", unread)}
      {tab("#/incidents/new", ["report"], "report", t("nav.report"), " report")}
      {tab("#/requests", ["requests", "requestNew", "request"], "doc", t("nav.requests"))}
      <button type="button" className="tab" onClick={onMenu} aria-expanded={menuOpen} aria-haspopup="dialog">
        <span className="tab-icon"><Icon name="menu" /></span>
        <span>{t("nav.menu")}</span>
      </button>
    </nav>
  );
}

/** メニュー: 全画面シートで全ページ（パソコンの縦メニューと同じ並び） */
export function MenuSheet({ open, onClose, me, route, onLogout }: { open: boolean; onClose: () => void; me: Me; route: Route; onLogout: () => void }) {
  const { t, lang, setLang } = useI18n();
  const { unread } = useNotifications();
  const navBase = useNavigate();
  const nav = (e: MouseEvent, href: string) => { navBase(e, href); onClose(); };
  return (
    <Sheet open={open} onClose={onClose} title={t("nav.menu")}>
      <nav aria-label={t("nav.label")}>
        {visibleGroups(me).map((g, i) => (
          <div className="nav-group" key={g.label ?? i}>
            {g.label && <p className="nav-group-label" id={`mnavg-${i}`}>{t(g.label)}</p>}
            <ul className="nav-list" aria-labelledby={g.label ? `mnavg-${i}` : undefined}>
              {g.items.map((it) => (
                <li key={it.href}><NavLink item={it} route={route} onNavigate={nav} t={t} count={it.href === "#/notifications" ? unread : undefined} /></li>
              ))}
            </ul>
          </div>
        ))}
        <div className="nav-group">
          <p className="nav-group-label">{me.displayName}</p>
          <ul className="nav-list">
            <li>
              <a className="nav-link" href="#/settings" aria-current={route.name === "settings" ? "page" : undefined} onClick={(e) => nav(e, "#/settings")}>
                <Icon name="gear" /><span>{t("nav.settings")}</span>
              </a>
            </li>
          </ul>
        </div>
      </nav>
      <div className="cluster" style={{ marginTop: "var(--s4)" }}>
        <LangSwitch lang={lang} setLang={setLang} />
        <button type="button" onClick={() => { onClose(); onLogout(); }}><Icon name="logout" size={20} />{t("common.logout")}</button>
      </div>
    </Sheet>
  );
}
